// src/types/attachments.ts
// Updated interfaces for Firebase Storage integration

// Attachment type for categorizing attachments
export type AttachmentType = 'general' | 'proof_of_purchase' | 'product_picture';

export interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  // For Firebase Storage integration
  url?: string;           // Firebase Storage download URL
  storagePath?: string;   // Path in Firebase Storage
  uploadedAt: string;
  uploadedBy: string;

  // Attachment category type
  attachmentType?: AttachmentType;  // Defaults to 'general' if not specified

  // Legacy support for existing base64 data (for migration)
  data?: string;          // Base64 data (deprecated)
  isLegacy?: boolean;     // Flag to identify old attachments
  file?: File;            // Optional File object, e.g., during upload
}

// File upload progress interface
export interface FileUploadProgress {
  id: string; // Unique identifier for the upload progress item
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  stage: 'pending' | 'validating' | 'converting' | 'uploading' | 'completed' | 'error'; // Detailed stage
  error?: string;
  attachment?: Attachment;
  totalPages?: number; // For PDFs, total pages to convert
  convertedPages?: number; // For PDFs, pages converted so far
}

// File validation result
export interface FileValidationResult {
  valid: boolean;
  error?: string;
  warnings?: string[];
}

// Attachment management utilities
export class AttachmentManager {
  // Convert legacy base64 attachments to Firebase Storage
  static async migrateLegacyAttachment(
    attachment: Attachment,
    projectId: string,
    itemId: string,
    userId: string
  ): Promise<Attachment> {
    if (!attachment.data || !attachment.isLegacy) {
      return attachment;
    }

    try {
      // Convert base64 to file
      const base64Data = attachment.data.split(',')[1];
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const file = new File([bytes], attachment.name, {
        type: attachment.type
      });

      // Upload to Firebase Storage
      const { FileStorageService } = await import('../services/fileStorageService');
      const storedAttachment = await FileStorageService.uploadFile(
        file,
        projectId,
        itemId,
        userId,
        attachment.id
      );

      // Return updated attachment without base64 data
      return {
        ...attachment,
        url: storedAttachment.url,
        storagePath: storedAttachment.storagePath,
        uploadedBy: storedAttachment.uploadedBy,
        data: undefined,  // Remove base64 data
        isLegacy: false   // Mark as migrated
      };
    } catch {
      // Return original attachment if migration fails
      return attachment;
    }
  }

  // Validate file before upload
  static validateFile(file: File): FileValidationResult {
    const warnings: string[] = [];

    // Size validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is ${maxSize / 1024 / 1024}MB.`
      };
    }

    // Type validation
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'application/zip',
      'application/x-zip-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type "${file.type}" is not allowed. Please use: images, PDF, Office documents, text files, or ZIP archives.`
      };
    }

    // Size warnings
    if (file.size > 5 * 1024 * 1024) { // 5MB
      warnings.push(`Large file size (${(file.size / 1024 / 1024).toFixed(1)}MB). Upload may take some time.`);
    }

    // Name validation
    if (file.name.length > 100) {
      warnings.push('File name is very long. Consider using a shorter name.');
    }

    return {
      valid: true,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Format file size for display
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Check if attachment is an image that can be previewed
  static canPreviewImage(attachment: Attachment): boolean {
    if (!attachment || !attachment.type) return false;
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return imageTypes.includes(attachment.type.toLowerCase());
  }

  // Clean up stored files when an item is deleted (best-effort)
  static async cleanupAttachments(attachments: Attachment[]): Promise<void> {
    const { FileStorageService } = await import('../services/fileStorageService');
    const storagePaths = attachments
      .filter(att => att.storagePath && !att.isLegacy)
      .map(att => att.storagePath!);
    if (storagePaths.length > 0) {
      try {
        await FileStorageService.deleteFiles(storagePaths);
      } catch {
        // Missing or undeletable files must not block the item deletion
      }
    }
  }

  // Get download URL for an attachment
  static getDownloadUrl(attachment: Attachment): string | undefined {
    if (attachment.url) {
      return attachment.url;
    }
    // Legacy attachments store a data URL in `data`
    if (attachment.isLegacy && attachment.data) {
      return attachment.data;
    }
    return undefined;
  }
}
